/**
 * Checks if an object is empty.
 * @param obj - The object to check.
 * @returns A boolean value indicating whether the object is empty.
 */
export const isEmptyObject = (obj: any): boolean =>
  !(obj && Object.keys(obj).length !== 0 && Object.getPrototypeOf(obj) === Object.prototype);

/**
 * Returns the position of an element in an array.
 * @param index - The index of the element.
 * @param length - The length of the array (optional).
 * @returns The position of the element in the array.
 */
export const ArrayElemPosition = (index: number, length?: number): string => {
  if (index === 0) return 'first';
  if (length && index === length - 1) return 'last';
  return '';
};
/**
 * Capitalizes the first letter of a string.
 *
 * @param {string} str - The input string.
 * @returns {string} The input string with the first letter capitalized.
 */
export const capitalizeFirstLetter = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Converts a number to a currency format with commas.
 * @param {number} x - The number to convert.
 * @returns {string} The number in currency format.
 */
export const convertCurrencyFormat = (x: number): string => {
  const parts = x?.toString().split('.');
  if (!!parts?.length) {
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts?.join('.');
  }
  return x?.toString();
};

/**
 * Converts a number to a currency format with specified decimal places.
 * @param x - The number to be converted.
 * @param decimals - The number of decimal places to include in the converted currency format. Default is 2.
 * @returns The number converted to a currency format.
 */
export const convertCurrencyFormatWithDecimals = (x: any, decimals: number = 2) => {
  var parts = x.toString().split('.');
  if (!!parts?.length) {
    parts[0] = parts[0]?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (parts[1]) {
      parts[1] = parts[1].padEnd(decimals, '0').slice(0, decimals);
    } else {
      parts[1] = '0'.repeat(decimals);
    }
    return parts?.join('.');
  }
  return x;
};

/**
 * Debounces a function and returns a promise that resolves with the result.
 * @param callback - The function to debounce.
 * @param wait - The debounce wait time in milliseconds.
 * @returns A promise that resolves with the result of the debounced function.
 */
export function debounce<T extends unknown[], U>(
  callback: (...args: T) => PromiseLike<U> | U,
  wait: number,
): (...args: T) => Promise<U> {
  let timer: NodeJS.Timeout | undefined;
  return (...args: T): Promise<U> => {
    clearTimeout(timer);
    return new Promise((resolve) => {
      timer = setTimeout(() => resolve(callback(...args)), wait);
    });
  };
}

/**
 * Removes inner elements from an array and returns a new array
 * containing only the first and last elements.
 *
 * @template T - The type of elements in the array.
 * @param {T[]} arr - The array from which to remove inner elements.
 * @returns {T[]} - A new array containing only the first and last elements.
 */
export const removeInnerElements = <T>(arr: T[]): T[] => arr.slice(0, 1).concat(arr.slice(-1));

/**
 * Checks if all values in an object are empty strings.
 * @param obj - The object to check.
 * @returns A boolean value indicating whether all values in the object are empty strings.
 */
export const objectValuesAreEmptyStrings = (obj: Record<string, string>): boolean => {
  return Object.values(obj).every((val) => val === '');
};

/**
 * Groups an array of objects by a specified key.
 * @param arr - The array of objects to be grouped.
 * @param key - The property name to group by.
 * @returns An object where the keys are unique values of the specified key and the values are arrays of objects with that key value.
 */
export const groupByMap = (arr: any[], key: string): Record<string, any[]> => {
  const map = new Map<string, any[]>();
  arr?.forEach((obj) => {
    const keyValue = obj[key];
    if (!map.has(keyValue)) {
      map.set(keyValue, []);
    }
    map.get(keyValue)?.push(obj);
  });
  return Object.fromEntries(map);
};

/**
 * Moves the selected object to the front of the array based on a specified key.
 * @param arr - The array of objects.
 * @param key - The key to match the selected object.
 * @returns The modified array with the selected object moved to the front.
 */
export const moveSelectedToFront = (arr: any[], key: string): any[] => {
  const selectedIndex = arr.findIndex((obj) => obj.value === key);
  if (selectedIndex === 0) {
    return arr;
  }
  if (selectedIndex > -1) {
    const [selectedObject] = arr.splice(selectedIndex, 1);
    arr.unshift(selectedObject);
  }
  return arr;
};

/**
 * Converts an array of strings into an object.
 * Each string in the array should be in the format "key: value".
 * The resulting object will have the keys and values extracted from the strings.
 * @param arr - The array of strings to convert.
 * @returns An object with keys and values extracted from the array of strings.
 */
export const arrayToObject = (arr: any[]) => {
  const obj: any = {};
  for (let i = 0; i < arr.length; i++) {
    const [key, value] = arr[i].split(': ');
    obj[key.trim()] = value.trim();
  }
  return obj;
};

/**
 * Removes HTML tags from a given text.
 *
 * @param {string} text - The text containing HTML tags.
 * @returns {string} - The text with HTML tags removed.
 */
export const removeHtmlTags = (text: string): string => {
  const regex = /<[^>]*>/g;
  return text.replace(regex, '');
};

/**
 * Scrolls the element with the specified ID into view.
 * @param {string} id - The ID of the element to scroll into view.
 * @returns {void}
 */
export const scrollIntoView = (id: string): void => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  }
};

/**
 * Rounds a number to the nearest whole number.
 * @param {number} num - The number to round.
 * @returns {number} The rounded whole number.
 */
export const roundToNearestWholeNumber = (num: number): number => {
  return Math.round(num);
};

/**
 * Generate an URL for a PDF document which is created from Base64 data.
 * @param {any} pdfContent - pdf Base64 Content.
 * @returns {any} an URL for a PDF document.
 */
export const getBase64PDFfileURL = (pdfContent: any): any => {
  const base64pdf = (pdfContent) || null;
  if (base64pdf) {
    const byteCharacters = atob(base64pdf);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });
    const base64PDFfileURL = URL.createObjectURL(blob);
    return base64PDFfileURL;
  }
  return null;
}

export const parseContentStackData = (contentType: any, mapID: string, selectedKey: string, selectedValue: string) => {
  return (
    Object.entries(groupByMap(contentType, mapID))
      .flatMap(([key, value]) => {
        return {
          title: key,
          selected: key === selectedKey,
          value: key.replaceAll(' ', '_'),
          contents: value,
        };
      })
      .filter((item: any) => {
        if (item?.value === selectedValue) {
          return item;
        }
        return null;
      })
  );
}

/**
 * Find ContentStack Accordion Content data based on UID.
 * @param {any} contentStackData - ContentStack data.
 * @param {string} uid - UID of the content.
 * @returns {any[]} Accordion Content data.
 */
export const getContentStackAccordionContents = (contentStackData: any, uid: string) => {
    const accordion_contents: any[] = [];
    const infoForms = contentStackData?.tabbed_accordion_content?.find((item: any) => item.uid === uid);
    const accordion_content = infoForms?.accordion_content;
    accordion_content?.map((item: any) => {
      const _content_uid = item.uid;
      const _content_type_uid = item._content_type_uid;
      const _content = _content_type_uid === 'accordion_content'
        ? contentStackData.accordion_content : contentStackData.tabbed_accordion_content;
      const _accordion_content = _content.find((item: any) => {
        return item.uid === _content_uid
      });
      accordion_contents.push(_accordion_content);
    });
    return accordion_contents;
  }

  export const getContentFromUID = (uid: string, _contentStackData: any, cb?: () => void): any => {
    const data = [
      ...(_contentStackData?.accordion_content ?? []),
      ...(_contentStackData?.tabbed_accordion_content ?? []),
    ];
    let content: any = null;
    if (data?.length > 0) {
      if (data?.find((item: any) => item?.uid === uid)) {
        content = data?.find((item: any) => item?.uid === uid);
      } else {
        if (cb) {
          cb();
        }
      }
    }
    return content;
  };
